import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";

const CORES = [
  "Verde",
  "Azul",
  "Amarelo",
  "Laranja",
  "Vermelho",
  "Roxo",
  "Rosa",
  "Cinza",
];

interface ColorPickerProps {
  colorValue: string;
  keyIndex: number;
  colorKey: string;
  openColorOptionIndex: number | null;
  setOpenColorOptionIndex: (index: number | null) => void;
  getAllColors: () => Record<string, { [key: string]: string | boolean }[]>;
  handleColorPickerSelection: (key: string, colorValue: string) => void;
  getColorFromPallet: (index: number) => string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  colorValue,
  keyIndex,
  openColorOptionIndex,
  setOpenColorOptionIndex,
  getAllColors,
  handleColorPickerSelection,
  getColorFromPallet,
}) => {
  const theme = useTheme();
  return (
    <Box>
      <IconButton
        onClick={() =>
          setOpenColorOptionIndex(
            openColorOptionIndex === keyIndex ? null : keyIndex
          )
        }
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: colorValue
              ? colorValue
                  .split(".")
                  .reduce(
                    (acc: any, curr: string) => (acc ? acc[curr] : undefined),
                    theme.palette.cromatic
                  ) || getColorFromPallet(keyIndex)
              : getColorFromPallet(keyIndex),
          }}
        ></Box>
      </IconButton>
      {openColorOptionIndex === keyIndex && (
        <Box
          sx={{
            zIndex: 10,
            marginBottom: 2,
            position: "absolute",
            borderRadius: 1,
            background: theme.palette.background.paper,
            width: "90%",
            bottom: "100%",
            right: 1,
            padding: 1,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {Object.entries(getAllColors())
            .slice(0, CORES.length)
            .map(([colorGroup, colorList], colorGroupIndex) => (
              <Box key={colorGroup} sx={{ marginBottom: 1, paddingX: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {(() => {
                    const mainColorObj = colorList.find(
                      (colorObj) => colorObj.main
                    );
                    if (!mainColorObj) return null;
                    const colorKey = Object.keys(mainColorObj)[0];
                    const colorVal = mainColorObj[colorKey] as string;
                    return (
                      <IconButton
                        key={colorKey}
                        size="small"
                        sx={{
                          backgroundColor: colorVal,
                          marginBottom: 1,
                          width: 40,
                          height: 40,
                          "&:hover": {
                            backgroundColor: colorVal,
                            border: 2,
                            borderColor: theme.palette.text.primary,
                          },
                        }}
                        onClick={() => {
                          handleColorPickerSelection(colorKey, colorKey);
                          setOpenColorOptionIndex(null);
                        }}
                      />
                    );
                  })()}
                  <Typography fontSize={"small"} sx={{ marginBottom: 0.5 }}>
                    {CORES[colorGroupIndex] || colorGroup}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {colorList
                    .filter((colorObj) => !colorObj.main)
                    .map((colorObj) => {
                      const colorKey = Object.keys(colorObj)[0];
                      const colorVal = colorObj[colorKey] as string;
                      return (
                        <IconButton
                          key={colorKey}
                          size="small"
                          sx={{
                            backgroundColor: colorVal,
                            width: 28,
                            height: 28,
                            "&:hover": {
                              backgroundColor: colorVal,
                              border: 2,
                              borderColor: theme.palette.text.primary,
                            },
                          }}
                          onClick={() => {
                            handleColorPickerSelection(colorKey, colorKey);
                            setOpenColorOptionIndex(null);
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default ColorPicker;
